import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  Typography,
  CircularProgress,
  Avatar,
  useTheme,
  InputAdornment,
} from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { DrawRequestMessageModel } from '@models/drawRequests/DrawRequestMessageModel';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
import { MESSAGE_TIMESTAMP_FORMAT_OPTIONS } from '@constants/drawManagerConstants';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { clearRouteHistory } from '@services/routePersistenceService';
import { colors } from '@theme/colors';
import { useApp } from '@context/AppContext';
import { useDrawRequestMessages } from '@context/DrawRequestMessageContext';

interface CreateDrawMessageProps {
  onMessageAdd?: (message: DrawRequestMessageModel) => void;
  pendingMessages?: DrawRequestMessageModel[];
  onMessageRemove?: (index: number) => void;
}

const MessagesCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  backgroundColor: colors.grey[200],
  padding: theme.spacing(2),
  height: '369px',
  display: 'flex',
  flexDirection: 'column',
}));

const MessagesHeader = styled(Typography)(({ theme }) => ({
  fontFamily: 'Epilogue',
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  mb: 3,
  flexShrink: 0,
}));

const MessagesContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  backgroundColor: colors.grey[100],
}));

const MessageBubble = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: colors.grey[100],
  borderRadius: '18px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  maxWidth: '100%',
  wordBreak: 'break-word',
  '& .message-text': {
    ...theme.typography.body2,
    fontSize: '13px',
    fontWeight: 400,
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
}));

const renderEmptyMessages = () => (
  <ListItem>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ textAlign: 'center', width: '100%' }}
    >
      No messages yet. Start the conversation!
    </Typography>
  </ListItem>
);

const renderMessageAvatar = (isBorrower: boolean, theme: Theme) => {
  if (!isBorrower) return null;

  return (
    <Avatar
      sx={{
        width: 24,
        height: 24,
        fontSize: '12px',
        bgcolor: theme.palette.primary.main,
      }}
    >
      B
    </Avatar>
  );
};

const renderMessageBubble = (
  msg: DrawRequestMessageModel,
  isBorrower: boolean
) => (
  <MessageBubble
    sx={{
      borderRadius: isBorrower ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    }}
  >
    <Typography variant="body2" component="div" className="message-text">
      {msg.message || ''}
    </Typography>
  </MessageBubble>
);

const renderMessageTimestamp = (
  msg: DrawRequestMessageModel,
  isBorrower: boolean
) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      mt: 0.5,
      alignSelf: isBorrower ? 'flex-end' : 'flex-start',
      fontSize: '11px',
      px: 1,
    }}
  >
    {msg.sentDateTime
      ? new Date(msg.sentDateTime).toLocaleString(
          MESSAGE_TIMESTAMP_FORMAT_OPTIONS.locale,
          MESSAGE_TIMESTAMP_FORMAT_OPTIONS.options
        )
      : 'Pending'}
  </Typography>
);

const renderMessage = (
  msg: DrawRequestMessageModel,
  index: number,
  theme: Theme,
  onMessageRemove?: (index: number) => void
) => {
  const isBorrower = msg.originatorType === OriginatorTypes.Borrower;

  return (
    <ListItem
      key={index}
      sx={{
        flexDirection: 'column',
        alignItems: isBorrower ? 'flex-end' : 'flex-start',
        padding: 0,
        marginBottom: 1,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          width: '100%',
          gap: 1,
          flexDirection: isBorrower ? 'row-reverse' : 'row',
        }}
      >
        {!isBorrower && renderMessageAvatar(isBorrower, theme)}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isBorrower ? 'flex-end' : 'flex-start',
            width: '85%',
          }}
        >
          {renderMessageBubble(msg, isBorrower)}
          {renderMessageTimestamp(msg, isBorrower)}
          {onMessageRemove && (
            <Button
              size="small"
              color="error"
              onClick={() => onMessageRemove(index)}
              sx={{ mt: 0.5, fontSize: '10px', minWidth: 'auto', px: 1 }}
            >
              Remove
            </Button>
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

const renderMessagesList = (
  messages: DrawRequestMessageModel[],
  messagesEndRef: React.RefObject<HTMLDivElement>,
  theme: Theme,
  onMessageRemove?: (index: number) => void
) => (
  <List>
    {messages.map((msg, index) =>
      renderMessage(msg, index, theme, onMessageRemove)
    )}
    {messages.length === 0 && renderEmptyMessages()}
    <div ref={messagesEndRef} />
  </List>
);

const renderMessageInput = (
  message: string,
  isSending: boolean,
  inputRef: React.RefObject<HTMLInputElement>,
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  onKeyPress: (event: React.KeyboardEvent) => void,
  onSendMessage: () => void,
  onFocus?: () => void
) => (
  <InputContainer>
    <TextField
      ref={inputRef}
      fullWidth
      multiline
      maxRows={4}
      placeholder="Type a message..."
      value={message}
      onChange={onInputChange}
      onKeyPress={onKeyPress}
      disabled={isSending}
      variant="outlined"
      size="small"
      slotProps={{
        htmlInput: {
          maxLength: 2000,
        },
        input: {
          style: { fontSize: '14px' },
          onFocus: onFocus,
          endAdornment: (
            <InputAdornment position="end">
              <Box
                component="button"
                type="button"
                disabled={!message.trim() || isSending}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (message.trim() && !isSending) {
                    onSendMessage();
                  }
                }}
                onTouchStart={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (message.trim() && !isSending) {
                    onSendMessage();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor:
                    message.trim() && !isSending ? 'pointer' : 'not-allowed',
                  opacity: message.trim() && !isSending ? 1 : 0.5,
                  border: 'none',
                  background: 'transparent',
                  padding: '4px',
                  minWidth: '32px',
                  minHeight: '32px',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  '&:disabled': {
                    cursor: 'not-allowed',
                  },
                  '&:active': {
                    opacity: 0.7,
                  },
                }}
              >
                {isSending ? (
                  <CircularProgress size={20} />
                ) : (
                  <ArrowUpward
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: 20,
                      backgroundColor: 'primary.main',
                      borderRadius: '50%',
                      padding: '2px',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                )}
              </Box>
            </InputAdornment>
          ),
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '20px',
          backgroundColor: colors.grey[200],
        },
      }}
    />
  </InputContainer>
);

function CreateDrawMessageContent({
  onMessageAdd,
  pendingMessages = [],
  onMessageRemove,
}: CreateDrawMessageProps) {
  const theme = useTheme();
  const { isTabletScreen } = useApp();
  const isMobile = isTabletScreen;
  const location = useLocation();
  const navigate = useNavigate();
  const { setIntentionalPath } = useRoutePersistence();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRoute = location.pathname === PrivateRoutes.CREATE_DRAW_MESSAGE;

  // Use context when it's a route (mobile), otherwise use props (desktop embedded)
  const contextMessages = useDrawRequestMessages();
  const effectivePendingMessages = isRoute
    ? contextMessages.pendingMessages
    : pendingMessages;
  const effectiveOnMessageAdd = isRoute
    ? contextMessages.addMessage
    : onMessageAdd;
  const effectiveOnMessageRemove = isRoute
    ? contextMessages.removeMessage
    : onMessageRemove;

  const handleBackClick = () => {
    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;
    const budgetItems = location.state?.budgetItems;
    const currentMessages = isRoute
      ? contextMessages.pendingMessages
      : pendingMessages;

    const isValidFromPath =
      fromPath &&
      fromPath !== PrivateRoutes.ROOT &&
      fromPath !== location.pathname;

    if (isValidFromPath) {
      navigate(fromPath, {
        state: {
          expandedLoan: expandedLoan,
          budgetItems: budgetItems,
          pendingMessages: currentMessages,
        },
      });
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        clearRouteHistory();
        setIntentionalPath(PrivateRoutes.ROOT);
        navigate(PrivateRoutes.ROOT, {
          replace: true,
          state: { scrollTo: 'your-loans-tile' },
        });
      }
    }
  };

  useEffect(() => {
    if (isRoute && location.state?.pendingMessages) {
      contextMessages.setPendingMessages(location.state.pendingMessages);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [effectivePendingMessages]);

  const handleInputFocus = () => {
    if (isMobile && inputRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        });
      });
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setIsSending(true);

    try {
      const newMessage: DrawRequestMessageModel = {
        message: message.trim(),
        sentDateTime: new Date(),
        originatorType: OriginatorTypes.Borrower,
        recipientType: RecipientTypes.Company,
      };

      effectiveOnMessageAdd?.(newMessage);
      setMessage('');
    } catch (error) {
      console.error('Error creating message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMessage(value);
  };

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        mx: 'auto',
        ...(isRoute && { p: 2, backgroundColor: 'teal.200' }),
      }}
    >
      {isRoute && (
        <Box
          sx={{
            mt: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={handleBackClick} className="back-button">
            {'< Back'}
          </Button>
        </Box>
      )}
      <MessagesCard
        elevation={0}
        sx={{
          height: isRoute ? 'calc(100vh - 252px)' : '369px',
        }}
      >
        <MessagesHeader>Messages</MessagesHeader>

        <MessagesContainer>
          {renderMessagesList(
            effectivePendingMessages,
            messagesEndRef,
            theme,
            effectiveOnMessageRemove
          )}
        </MessagesContainer>

        {renderMessageInput(
          message,
          isSending,
          inputRef,
          handleInputChange,
          handleKeyPress,
          handleSendMessage,
          handleInputFocus
        )}
      </MessagesCard>
    </Box>
  );
}

export default function CreateDrawMessage(props: CreateDrawMessageProps) {
  return <CreateDrawMessageContent {...props} />;
}
